import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RewardsAccount, RewardPrize, RewardTransaction, RewardsClaim } from '@shared/sdk-v1';
import { getUser } from '../state/authSlice';
import { getCourier } from '../state/courierSlice';
import { useRewardsApi } from './useRewardsApi';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { extractApiErrorMessage } from '../utils/apiError';

export const useRewards = () => {
  const user = useSelector(getUser);
  const courier = useSelector(getCourier);
  const rewardSetting = courier?.rewardSetting;
  const [rewardsAccount, setRewardsAccount] = useState<RewardsAccount | null>(null);
  const [availableRewards, setAvailableRewards] = useState<RewardPrize[]>([]);
  const [transactions, setTransactions] = useState<RewardTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [claims, setClaims] = useState<RewardsClaim[]>([]);
  const rewardsApi = useRewardsApi();

  useEffect(() => {
    const fetchRewardsData = async () => {
      if (!user?.id || !courier?.id) return;

      try {
        setIsLoading(true);
        setError(null);

        const [account, rewards, claimsList, transactionsList] = await Promise.all([
          rewardsApi.getRewardsAccount(user.id, courier.id),
          rewardsApi.getAvailableRewards(courier.id),
          rewardsApi.getRewardsClaims(user.id, courier.id),
          rewardsApi.getRewardTransactions(user.id, courier.id)
        ]);

        setRewardsAccount(account);
        setAvailableRewards(rewards);
        setClaims(claimsList);
        setTransactions(transactionsList);

      } catch (err) {
        console.error('Error fetching rewards data:', err);
        setError('Failed to load rewards data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRewardsData();
  }, [user?.id, courier?.id]);

  const getReferralCode = () => {
    return user?.id?.toString() || '';
  };

  const getReferralLink = () => {
    return `${window.location.protocol}//${window.location.host}/auth?referralId=${getReferralCode()}`;
  };

  const claimReward = async (prizeId: number) => {
    if (!user?.id || !courier?.id) return;

    try {
      setIsLoading(true);
      const success = await rewardsApi.claimReward(user.id, courier.id, prizeId);
      
      // The server answers "not enough points" / "no account" with a falsy
      // body rather than an error on older builds, so only an explicit true
      // means a claim now exists for the courier to see.
      if (success !== true) {
        showErrorToast({
          title: 'Reward not claimed',
          message: "You don't have enough points for this reward yet.",
        });
        return false;
      }

      // Refresh all data after successful claim
      const [account, claimsList, transactionsList] = await Promise.all([
        rewardsApi.getRewardsAccount(user.id, courier.id),
        rewardsApi.getRewardsClaims(user.id, courier.id),
        rewardsApi.getRewardTransactions(user.id, courier.id)
      ]);

      setRewardsAccount(account);
      setClaims(claimsList);
      setTransactions(transactionsList);

      showSuccessToast({
        title: 'Reward claimed successfully',
        message: 'You have claimed a reward',
      });

      return true;
    } catch (err) {
      console.error('Error claiming reward:', err);

      showErrorToast({
        title: 'Reward not claimed',
        message: await extractApiErrorMessage(err, 'Please try again'),
      });

      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    rewardsAccount,
    availableRewards,
    transactions,
    claims,
    isLoading,
    error,
    getReferralCode,
    getReferralLink,
    claimReward,
    rewardSetting
  };
};